import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import scrapeJobService from '../../services/scrapeJobService';

const initialState = {
  scrapeJobs: [],
  selectedJob: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasMore: false,
  },
  filters: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
  updateLoading: {},
  deleteLoading: {},
};

// Get all scrape jobs
export const fetchScrapeJobs = createAsyncThunk(
  'scrapeJob/fetchScrapeJobs',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await scrapeJobService.getScrapeJobs(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch scrape jobs');
    }
  }
);

// Get scrape job by ID
export const fetchScrapeJobById = createAsyncThunk(
  'scrapeJob/fetchScrapeJobById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await scrapeJobService.getScrapeJobById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch scrape job');
    }
  }
);

// Update scrape job
export const updateScrapeJob = createAsyncThunk(
  'scrapeJob/updateScrapeJob',
  async ({ id, jobData }, { rejectWithValue }) => {
    try {
      const response = await scrapeJobService.updateScrapeJob(id, jobData);
      return { id, jobData, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update scrape job');
    }
  }
);

// Delete scrape job
export const deleteScrapeJob = createAsyncThunk(
  'scrapeJob/deleteScrapeJob',
  async (id, { rejectWithValue }) => {
    try {
      await scrapeJobService.deleteScrapeJob(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete scrape job');
    }
  }
);

const scrapeJobSlice = createSlice({
  name: 'scrapeJob',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedJob: (state) => {
      state.selectedJob = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch scrape jobs
      .addCase(fetchScrapeJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScrapeJobs.fulfilled, (state, action) => {
        state.loading = false;
        const newJobs = action.payload.scrapeJobs || [];
        const currentPage = action.payload.pagination?.currentPage || 1;

        // If it's page 1, replace the data. Otherwise, append it for "Load More" functionality.
        if (currentPage === 1) {
          state.scrapeJobs = newJobs;
        } else {
          state.scrapeJobs = [...state.scrapeJobs, ...newJobs];
        }

        state.pagination = {
          currentPage: action.payload.pagination?.currentPage || 1,
          totalPages: action.payload.pagination?.totalPages || 1,
          totalCount: action.payload.pagination?.totalCount || 0,
          limit: action.payload.pagination?.limit || 10,
          hasMore: action.payload.pagination?.hasMore || false,
        };
      })
      .addCase(fetchScrapeJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch scrape job by ID
      .addCase(fetchScrapeJobById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScrapeJobById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedJob = action.payload.scrapeJob || action.payload;
      })
      .addCase(fetchScrapeJobById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update scrape job
      .addCase(updateScrapeJob.pending, (state, action) => {
        const id = action.meta.arg.id;
        state.updateLoading[id] = true;
        state.error = null;
      })
      .addCase(updateScrapeJob.fulfilled, (state, action) => {
        const { id, jobData } = action.payload;
        state.updateLoading[id] = false;

        // Update job in the list
        const jobIndex = state.scrapeJobs.findIndex(job => job._id === id);
        if (jobIndex !== -1) {
          state.scrapeJobs[jobIndex] = {
            ...state.scrapeJobs[jobIndex],
            ...jobData
          };
        }

        // Update selected job if it's the same
        if (state.selectedJob && state.selectedJob._id === id) {
          state.selectedJob = {
            ...state.selectedJob,
            ...jobData
          };
        }
      })
      .addCase(updateScrapeJob.rejected, (state, action) => {
        const id = action.meta.arg.id;
        state.updateLoading[id] = false;
        state.error = action.payload;
      })

      // Delete scrape job
      .addCase(deleteScrapeJob.pending, (state, action) => {
        const id = action.meta.arg;
        state.deleteLoading[id] = true;
        state.error = null;
      })
      .addCase(deleteScrapeJob.fulfilled, (state, action) => {
        const id = action.payload;
        state.deleteLoading[id] = false;

        // Remove job from the list
        state.scrapeJobs = state.scrapeJobs.filter(job => job._id !== id);
        state.pagination.totalCount -= 1;

        // Clear selected job if it was deleted
        if (state.selectedJob && state.selectedJob._id === id) {
          state.selectedJob = null;
        }
      })
      .addCase(deleteScrapeJob.rejected, (state, action) => {
        const id = action.meta.arg;
        state.deleteLoading[id] = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearSelectedJob, clearError } = scrapeJobSlice.actions;

export default scrapeJobSlice.reducer;
